import AnalyticsIcon from '@mui/icons-material/Analytics';
import BusinessIcon from '@mui/icons-material/Business';
import CategoryIcon from '@mui/icons-material/Category';
import DashboardIcon from '@mui/icons-material/Dashboard';
import DiscountIcon from '@mui/icons-material/Discount';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import GroupsIcon from '@mui/icons-material/Groups';
import HubIcon from '@mui/icons-material/Hub';
import InboxIcon from '@mui/icons-material/Inbox';
import InventoryIcon from '@mui/icons-material/Inventory';
import LabelIcon from '@mui/icons-material/Label';
import LiveHelpIcon from '@mui/icons-material/LiveHelp';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import PeopleIcon from '@mui/icons-material/People';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import PublishIcon from '@mui/icons-material/Publish';
import RateReviewIcon from '@mui/icons-material/RateReview';
import ReceiptIcon from '@mui/icons-material/Receipt';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import SecurityIcon from '@mui/icons-material/Security';
import SettingsIcon from '@mui/icons-material/Settings';
import ShareIcon from '@mui/icons-material/Share';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import StoreIcon from '@mui/icons-material/Store';
import StraightenIcon from '@mui/icons-material/Straighten';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import TimelineIcon from '@mui/icons-material/Timeline';
import ViewKanbanIcon from '@mui/icons-material/ViewKanban';
import WorkIcon from '@mui/icons-material/Work';
import WorkspacesIcon from '@mui/icons-material/Workspaces';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import AssignmentIcon from '@mui/icons-material/Assignment';
import BugReportIcon from '@mui/icons-material/BugReport';
import CampaignIcon from '@mui/icons-material/Campaign';
import EventNoteIcon from '@mui/icons-material/EventNote';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import PaymentsIcon from '@mui/icons-material/Payments';

import type { NavWorkflowGroup } from '@/components/layout/Sidebar';
import type { ErpModule } from '@/api/platform.api';
import { FEATURE } from '@/core/features/featureCodes';
import { getModuleEntryBySlug, MODULE_ENTRIES } from '@/config/moduleEntries';
import { getModuleAdminNavItems, resolveModuleFromAdminPath } from '@/config/moduleDefaults';
import { ALL_LEGACY_ORG_REDIRECTS } from '@/config/modulePathMigration';
import { moduleSharedPath } from '@/config/moduleSharedPaths';

/** Canonical sidebar group ordering: dashboard → work → reports → administration */
export type AppNavConfig = {
  module: ErpModule;
  /** Primary sidebar heading — the workspace name */
  label: string;
  /** Secondary sidebar line — short scope summary related to the heading */
  subtitle?: string;
  groups: NavWorkflowGroup[];
};

function adminGroup(module: ErpModule): NavWorkflowGroup {
  return {
    id: 'administration',
    label: 'Administration',
    items: getModuleAdminNavItems(module),
  };
}

const APP_NAV_CONFIGS: AppNavConfig[] = [
  {
    module: 'POS',
    label: 'Point of Sale',
    groups: [
      {
        id: 'dashboard',
        label: 'Overview',
        pinned: true,
        items: [{ to: '/pos/dashboard', label: 'Dashboard', icon: DashboardIcon, end: true }],
      },
      {
        id: 'sales',
        label: 'Sales',
        items: [
          { to: '/pos', label: 'POS terminal', icon: PointOfSaleIcon, feature: FEATURE.POS_BILLING },
          { to: '/pos/invoices', label: 'Invoices', icon: ReceiptIcon, feature: FEATURE.POS_BILLING },
          { to: '/pos/held', label: 'Held bills', icon: ReceiptIcon, feature: FEATURE.POS_BILLING },
          { to: '/pos/shift', label: 'Cashier shifts', icon: ReceiptIcon, feature: FEATURE.POS_SHIFT_MANAGEMENT },
          { to: '/pos/returns', label: 'Returns', icon: ReceiptIcon, feature: FEATURE.POS_RETURNS },
          { to: '/pos/setup', label: 'POS hardware setup', icon: SettingsIcon, feature: FEATURE.POS_BILLING },
        ],
      },
      {
        id: 'catalog',
        label: 'Catalog',
        items: [
          { to: '/pos/stores', label: 'Stores', icon: StoreIcon },
          { to: '/pos/products', label: 'Products', icon: ShoppingBagIcon, feature: FEATURE.POS_BILLING },
          { to: '/pos/categories', label: 'Categories', icon: CategoryIcon, feature: FEATURE.POS_BILLING },
          { to: '/pos/brands', label: 'Brands', icon: LabelIcon, feature: FEATURE.POS_BILLING },
          { to: '/pos/units', label: 'Units', icon: StraightenIcon, feature: FEATURE.POS_BILLING },
          { to: '/pos/tax-rules', label: 'Tax rules', icon: ReceiptIcon, feature: FEATURE.POS_BILLING },
          { to: '/pos/store-products', label: 'Store products', icon: ShoppingBagIcon, feature: FEATURE.POS_BILLING },
          { to: '/pos/discounts', label: 'Discounts', icon: DiscountIcon, feature: FEATURE.POS_BILLING },
        ],
      },
      {
        id: 'inventory',
        label: 'Inventory',
        items: [
          { to: '/pos/inventory', label: 'Stock', icon: InventoryIcon, feature: FEATURE.INVENTORY },
          { to: '/pos/inventory/ledger', label: 'Adjustments & ledger', icon: InventoryIcon, feature: FEATURE.INVENTORY },
          { to: '/pos/product-alerts', label: 'Alerts', icon: NotificationsActiveIcon, feature: FEATURE.INVENTORY },
        ],
      },
      {
        id: 'customers',
        label: 'Customers',
        items: [{ to: '/pos/customers', label: 'Customers', icon: PeopleIcon }],
      },
      {
        id: 'reports',
        label: 'Reports',
        items: [
          { to: '/pos/reports', label: 'Business reports', icon: AnalyticsIcon, feature: FEATURE.REPORTING },
          { to: '/pos/terminal-reports', label: 'POS terminal reports', icon: AnalyticsIcon, feature: FEATURE.POS_BILLING },
          { to: '/pos/analytics', label: 'POS analytics', icon: AnalyticsIcon, feature: FEATURE.POS_ANALYTICS },
        ],
      },
      adminGroup('POS'),
    ],
  },
  {
    module: 'PROCUREMENT',
    label: 'Procurement',
    groups: [
      {
        id: 'dashboard',
        label: 'Overview',
        pinned: true,
        items: [
          {
            to: '/procurement',
            label: 'Dashboard',
            icon: RequestQuoteIcon,
            end: true,
            feature: FEATURE.PROCUREMENT_BASIC,
          },
        ],
      },
      {
        id: 'procure-to-pay',
        label: 'Procure to pay',
        items: [
          {
            to: '/procurement/purchase-requests',
            label: 'Purchase requests',
            icon: RequestQuoteIcon,
            feature: FEATURE.PROCUREMENT_BASIC,
          },
          { to: '/procurement/rfqs', label: 'RFQs', icon: RequestQuoteIcon, feature: FEATURE.PROCUREMENT_RFQ },
          {
            to: '/procurement/quotations',
            label: 'Quotations',
            icon: ReceiptIcon,
            feature: FEATURE.PROCUREMENT_RFQ,
          },
          {
            to: '/procurement/purchase-orders',
            label: 'Purchase orders',
            icon: ReceiptIcon,
            feature: FEATURE.PROCUREMENT_BASIC,
          },
          {
            to: '/procurement/goods-receipts',
            label: 'Goods receipts',
            icon: LocalShippingIcon,
            feature: FEATURE.PROCUREMENT_BASIC,
          },
          {
            to: '/procurement/vendor-bills',
            label: 'Vendor bills',
            icon: ReceiptIcon,
            feature: FEATURE.PROCUREMENT_BASIC,
          },
          {
            to: '/procurement/contracts',
            label: 'Contracts',
            icon: MenuBookIcon,
            feature: FEATURE.PROCUREMENT_BASIC,
          },
          {
            to: '/procurement/catalogs',
            label: 'Supplier catalogs',
            icon: ShoppingBagIcon,
            feature: FEATURE.PROCUREMENT_BASIC,
          },
        ],
      },
      {
        id: 'master-data',
        label: 'Master data',
        items: [
          { to: moduleSharedPath('PROCUREMENT', 'suppliers'), label: 'Suppliers', icon: LocalShippingIcon, feature: FEATURE.PROCUREMENT },
          { to: moduleSharedPath('PROCUREMENT', 'products'), label: 'Products', icon: ShoppingBagIcon, feature: FEATURE.POS_BILLING },
          { to: moduleSharedPath('PROCUREMENT', 'inventory'), label: 'Inventory', icon: InventoryIcon, feature: FEATURE.INVENTORY },
          { to: moduleSharedPath('PROCUREMENT', 'inventory/ledger'), label: 'Stock ledger', icon: InventoryIcon, feature: FEATURE.INVENTORY },
          { to: '/warehouse', label: 'Warehouse', icon: LocalShippingIcon, feature: FEATURE.WAREHOUSE },
        ],
      },
      {
        id: 'reports',
        label: 'Reports',
        items: [
          {
            to: '/procurement/reports',
            label: 'Procurement reports',
            icon: AnalyticsIcon,
            feature: FEATURE.PROCUREMENT_ADVANCED_ANALYTICS,
          },
        ],
      },
      {
        id: 'setup',
        label: 'Configuration',
        items: [
          {
            to: '/procurement/setup',
            label: 'Approval workflows',
            icon: SettingsIcon,
            feature: FEATURE.PROCUREMENT_APPROVALS,
          },
        ],
      },
      adminGroup('PROCUREMENT'),
    ],
  },
  {
    module: 'WAREHOUSE',
    label: 'Warehouse',
    groups: [
      {
        id: 'dashboard',
        label: 'Overview',
        pinned: true,
        items: [
          { to: '/warehouse', label: 'Dashboard', icon: LocalShippingIcon, end: true, feature: FEATURE.WAREHOUSE },
        ],
      },
      {
        id: 'locations',
        label: 'Locations',
        items: [
          { to: '/warehouse/setup', label: 'Locations & zones', icon: StoreIcon, feature: FEATURE.WAREHOUSE },
        ],
      },
      {
        id: 'movements',
        label: 'Movements',
        items: [
          { to: '/warehouse/operations', label: 'Operations', icon: LocalShippingIcon, feature: FEATURE.WAREHOUSE },
          { to: '/warehouse/transfers', label: 'Transfers', icon: LocalShippingIcon, feature: FEATURE.WAREHOUSE },
          { to: '/warehouse/cross-dock', label: 'Cross-dock', icon: LocalShippingIcon, feature: FEATURE.WAREHOUSE },
          { to: '/warehouse/dock', label: 'Dock', icon: LocalShippingIcon, feature: FEATURE.WAREHOUSE },
        ],
      },
      {
        id: 'picks',
        label: 'Picks',
        items: [
          { to: '/warehouse/picks', label: 'Pick tasks', icon: InventoryIcon, feature: FEATURE.WAREHOUSE },
          { to: '/warehouse/waves', label: 'Waves', icon: TimelineIcon, feature: FEATURE.WAREHOUSE },
        ],
      },
      {
        id: 'cycle-counts',
        label: 'Cycle counts',
        items: [
          { to: '/warehouse/cycle-counts', label: 'Cycle counts', icon: FactCheckIcon, feature: FEATURE.WAREHOUSE },
        ],
      },
      {
        id: 'reports',
        label: 'Reports',
        items: [
          { to: '/warehouse/labor', label: 'Labor productivity', icon: AnalyticsIcon, feature: FEATURE.WAREHOUSE },
        ],
      },
      {
        id: 'setup',
        label: 'Configuration',
        items: [
          { to: '/warehouse/mobile', label: 'Mobile WMS', icon: LocalShippingIcon, feature: FEATURE.WAREHOUSE },
        ],
      },
      adminGroup('WAREHOUSE'),
    ],
  },
  {
    module: 'CRM',
    label: 'CRM',
    groups: [
      {
        id: 'dashboard',
        label: 'Overview',
        pinned: true,
        items: [{ to: '/crm', label: 'Dashboard', icon: HubIcon, end: true, feature: FEATURE.CRM_BASIC }],
      },
      {
        id: 'leads',
        label: 'Leads',
        items: [{ to: '/crm/leads', label: 'Leads', icon: PeopleIcon, feature: FEATURE.CRM_BASIC }],
      },
      {
        id: 'contacts',
        label: 'Contacts',
        items: [{ to: '/crm/contacts', label: 'Contacts', icon: PeopleIcon, feature: FEATURE.CRM_BASIC }],
      },
      {
        id: 'companies',
        label: 'Companies',
        items: [{ to: '/crm/companies', label: 'Companies', icon: BusinessIcon, feature: FEATURE.CRM_BASIC }],
      },
      {
        id: 'deals',
        label: 'Deals',
        items: [
          { to: '/crm/deals', label: 'Deals', icon: WorkspacesIcon, feature: FEATURE.CRM_BASIC },
          { to: '/crm/pipelines', label: 'Pipelines', icon: WorkspacesIcon, feature: FEATURE.CRM_PIPELINE },
        ],
      },
      {
        id: 'activities',
        label: 'Activities',
        items: [
          { to: '/crm/followups', label: 'Follow-ups', icon: NotificationsActiveIcon, feature: FEATURE.CRM_BASIC },
          { to: '/crm/tasks', label: 'Tasks', icon: WorkIcon, feature: FEATURE.CRM_BASIC },
          { to: '/crm/campaigns', label: 'Campaigns', icon: CampaignIcon, feature: FEATURE.CRM_CAMPAIGNS },
        ],
      },
      {
        id: 'reports',
        label: 'Reports',
        items: [
          { to: '/crm/reports', label: 'Sales reports', icon: AnalyticsIcon, feature: FEATURE.CRM_ANALYTICS },
        ],
      },
      {
        id: 'setup',
        label: 'Configuration',
        items: [{ to: '/crm/setup', label: 'CRM setup', icon: SettingsIcon, feature: FEATURE.CRM_BASIC }],
      },
      adminGroup('CRM'),
    ],
  },
  {
    module: 'LEAD_GEN',
    label: 'Lead Generation',
    groups: [
      {
        id: 'dashboard',
        label: 'Overview',
        pinned: true,
        items: [{ to: '/lead-gen', label: 'Dashboard', icon: HubIcon, end: true, feature: FEATURE.LEAD_GEN_BASIC }],
      },
      {
        id: 'acquisition',
        label: 'Acquisition',
        items: [
          { to: '/lead-gen/forms', label: 'Forms', icon: CampaignIcon, feature: FEATURE.LEAD_GEN_BASIC },
          {
            to: '/lead-gen/landing-pages',
            label: 'Landing pages',
            icon: PublishIcon,
            feature: FEATURE.LEAD_GEN_LANDING_PAGES,
          },
          { to: '/lead-gen/campaigns', label: 'Campaigns', icon: CampaignIcon, feature: FEATURE.LEAD_GEN_CAMPAIGNS },
          { to: '/lead-gen/sources', label: 'Lead sources', icon: CampaignIcon, feature: FEATURE.LEAD_GEN_BASIC },
          { to: '/lead-gen/webhooks', label: 'Ad webhooks', icon: HubIcon, feature: FEATURE.LEAD_GEN_BASIC },
        ],
      },
      {
        id: 'qualification',
        label: 'Qualification',
        items: [
          {
            to: '/lead-gen/visitor-tracking',
            label: 'Visitor tracking',
            icon: TimelineIcon,
            feature: FEATURE.LEAD_GEN_BASIC,
          },
          { to: '/lead-gen/scoring', label: 'Lead scoring', icon: RateReviewIcon, feature: FEATURE.LEAD_GEN_SCORING },
          { to: '/lead-gen/routing', label: 'Lead routing', icon: HubIcon, feature: FEATURE.LEAD_GEN_ROUTING },
          { to: '/lead-gen/enrichment', label: 'Enrichment', icon: RateReviewIcon, feature: FEATURE.LEAD_GEN_ABM },
          { to: '/lead-gen/ab-tests', label: 'A/B tests', icon: PublishIcon, feature: FEATURE.LEAD_GEN_LANDING_PAGES },
          { to: '/lead-gen/consent', label: 'GDPR & consent', icon: SecurityIcon, feature: FEATURE.LEAD_GEN_BASIC },
        ],
      },
      {
        id: 'reports',
        label: 'Reports',
        items: [
          {
            to: '/lead-gen/reports',
            label: 'Attribution & ROI',
            icon: AnalyticsIcon,
            feature: FEATURE.LEAD_GEN_ATTRIBUTION,
          },
        ],
      },
      adminGroup('LEAD_GEN'),
    ],
  },
  {
    module: 'MARKETING',
    label: 'Marketing',
    groups: [
      {
        id: 'dashboard',
        label: 'Overview',
        pinned: true,
        items: [
          { to: '/marketing', label: 'Dashboard', icon: HubIcon, end: true, feature: FEATURE.MARKETING_BASIC },
        ],
      },
      {
        id: 'campaigns',
        label: 'Campaigns & journeys',
        items: [
          {
            to: '/marketing/campaigns',
            label: 'Campaigns',
            icon: CampaignIcon,
            feature: FEATURE.MARKETING_EMAIL,
          },
          { to: '/marketing/journeys', label: 'Journeys', icon: AccountTreeIcon, feature: FEATURE.MARKETING_JOURNEY },
          { to: '/marketing/lifecycle', label: 'Lifecycle', icon: EventNoteIcon, feature: FEATURE.MARKETING_AUTOMATION },
          { to: '/marketing/templates', label: 'Templates', icon: PublishIcon, feature: FEATURE.MARKETING_EMAIL },
        ],
      },
      {
        id: 'audiences',
        label: 'Audiences',
        items: [
          { to: '/marketing/segments', label: 'Segments', icon: PeopleIcon, feature: FEATURE.MARKETING_BASIC },
          { to: moduleSharedPath('MARKETING', 'customers'), label: 'Customers', icon: PeopleIcon },
        ],
      },
      {
        id: 'reports',
        label: 'Reports',
        items: [
          {
            to: '/marketing/reports',
            label: 'Performance & ROI',
            icon: AnalyticsIcon,
            feature: FEATURE.MARKETING_ATTRIBUTION,
          },
        ],
      },
      {
        id: 'setup',
        label: 'Configuration',
        items: [
          { to: '/marketing/webhooks', label: 'ESP webhooks', icon: HubIcon, feature: FEATURE.MARKETING_EMAIL },
        ],
      },
      adminGroup('MARKETING'),
    ],
  },
  {
    module: 'HR',
    label: 'Human Resources',
    subtitle: 'People, time tracking & payroll',
    groups: [
      {
        id: 'dashboard',
        label: 'Overview',
        pinned: true,
        items: [{ to: '/hr', label: 'HR dashboard', icon: GroupsIcon, end: true, feature: FEATURE.HR_BASIC }],
      },
      {
        id: 'people',
        label: 'People',
        items: [
          { to: '/hr/employees', label: 'Employee directory', icon: GroupsIcon, feature: FEATURE.HR_BASIC },
          { to: '/hr/org', label: 'Reporting hierarchy', icon: AccountTreeIcon, feature: FEATURE.HR_BASIC },
        ],
      },
      {
        id: 'time-attendance',
        label: 'Time & attendance',
        items: [
          { to: '/hr/attendance', label: 'Attendance and leave', icon: FactCheckIcon, feature: FEATURE.HR_ATTENDANCE },
          { to: '/hr/shifts', label: 'Shifts', icon: FactCheckIcon, feature: FEATURE.HR_ATTENDANCE },
          { to: '/hr/overtime', label: 'Overtime requests', icon: FactCheckIcon, feature: FEATURE.HR_ATTENDANCE },
          { to: '/hr/devices', label: 'Biometric devices', icon: FactCheckIcon, feature: FEATURE.HR_ATTENDANCE },
        ],
      },
      {
        id: 'payroll',
        label: 'Payroll & compensation',
        items: [
          { to: '/hr/payroll', label: 'Payroll runs', icon: PaymentsIcon, feature: FEATURE.HR_BASIC },
          { to: '/hr/compensation', label: 'Salary & benefits', icon: PaymentsIcon, feature: FEATURE.HR_BASIC },
          { to: '/hr/statutory', label: 'Statutory filing', icon: PaymentsIcon, feature: FEATURE.HR_BASIC },
        ],
      },
      {
        id: 'expenses',
        label: 'Expenses & travel',
        items: [
          { to: '/hr/claims', label: 'Expense claims', icon: ReceiptIcon, feature: FEATURE.HR_BASIC },
          { to: '/hr/travel', label: 'Travel requests', icon: ReceiptIcon, feature: FEATURE.HR_BASIC },
        ],
      },
      {
        id: 'talent',
        label: 'Talent & performance',
        items: [
          { to: '/hr/recruitment', label: 'Recruitment', icon: WorkIcon, feature: FEATURE.HR_RECRUITMENT },
          { to: '/hr/onboarding', label: 'Onboarding & exit', icon: WorkIcon, feature: FEATURE.HR_BASIC },
          { to: '/hr/appraisal', label: 'Performance reviews', icon: AnalyticsIcon, feature: FEATURE.HR_APPRAISAL },
          { to: '/hr/feedback360', label: '360° feedback', icon: AnalyticsIcon, feature: FEATURE.HR_APPRAISAL },
          { to: '/hr/succession', label: 'Succession planning', icon: AnalyticsIcon, feature: FEATURE.HR_BASIC },
          { to: '/hr/training', label: 'Training & courses', icon: MenuBookIcon, feature: FEATURE.HR_BASIC },
        ],
      },
      {
        id: 'portals',
        label: 'Self-service portals',
        items: [
          { to: '/hr/ess', label: 'Employee self-service', icon: GroupsIcon, feature: FEATURE.HR_BASIC },
          { to: '/hr/mss', label: 'Manager self-service', icon: GroupsIcon, feature: FEATURE.HR_BASIC },
        ],
      },
      {
        id: 'reports',
        label: 'Reports',
        items: [{ to: '/hr/reports', label: 'HR analytics & dashboards', icon: AnalyticsIcon, feature: FEATURE.HR_BASIC }],
      },
      {
        id: 'setup',
        label: 'Setup & configuration',
        items: [
          { to: '/hr/setup', label: 'General HR settings', icon: SettingsIcon, feature: FEATURE.HR_BASIC },
          { to: '/hr/holidays', label: 'Public holidays', icon: EventNoteIcon, feature: FEATURE.HR_BASIC },
          { to: '/hr/master-data', label: 'HR master data', icon: EventNoteIcon, feature: FEATURE.HR_BASIC },
          { to: '/hr/admin', label: 'HR administration', icon: SecurityIcon, feature: FEATURE.HR_BASIC },
        ],
      },
      adminGroup('HR'),
    ],
  },
  {
    module: 'FINANCE',
    label: 'Finance',
    groups: [
      {
        id: 'dashboard',
        label: 'Overview',
        pinned: true,
        items: [
          { to: '/finance', label: 'CFO dashboard', icon: AccountBalanceIcon, end: true, feature: FEATURE.FINANCE_BASIC },
        ],
      },
      {
        id: 'receivables',
        label: 'Receivables',
        items: [
          { to: '/finance/receivables', label: 'Receivables home', icon: ReceiptIcon, end: true, feature: FEATURE.FINANCE_BASIC },
          { to: '/finance/invoices', label: 'Invoices', icon: ReceiptIcon, feature: FEATURE.FINANCE_BASIC },
          { to: '/finance/payments', label: 'Customer payments', icon: PaymentsIcon, feature: FEATURE.FINANCE_BASIC },
          { to: '/finance/collections', label: 'Collections center', icon: NotificationsActiveIcon, feature: FEATURE.FINANCE_BASIC },
        ],
      },
      {
        id: 'payables',
        label: 'Payables',
        items: [
          { to: '/finance/payables', label: 'Payables home', icon: PaymentsIcon, end: true, feature: FEATURE.FINANCE_BASIC },
          { to: '/finance/payables/ap', label: 'AP & payment runs', icon: PaymentsIcon, feature: FEATURE.FINANCE_BASIC },
          { to: '/finance/expenses', label: 'Expenses', icon: PaymentsIcon, feature: FEATURE.FINANCE_BASIC },
          { to: '/finance/documents', label: 'Document capture', icon: ReceiptIcon, feature: FEATURE.FINANCE_BASIC },
        ],
      },
      {
        id: 'treasury',
        label: 'Treasury',
        items: [
          { to: '/finance/treasury', label: 'Treasury dashboard', icon: AccountBalanceIcon, end: true, feature: FEATURE.FINANCE_BASIC },
          { to: '/finance/treasury/forecast', label: 'Cash forecast', icon: TimelineIcon, feature: FEATURE.FINANCE_BASIC },
          { to: '/finance/banking', label: 'Banking', icon: AccountBalanceIcon, feature: FEATURE.FINANCE_BASIC },
        ],
      },
      {
        id: 'assets',
        label: 'Assets',
        items: [
          { to: '/finance/assets', label: 'Fixed assets', icon: BusinessIcon, end: true, feature: FEATURE.FINANCE_ASSETS },
        ],
      },
      {
        id: 'budgets',
        label: 'Budgets',
        items: [
          { to: '/finance/budgets', label: 'Budget plans', icon: AnalyticsIcon, end: true, feature: FEATURE.FINANCE_BUDGETING },
        ],
      },
      {
        id: 'tax',
        label: 'Tax',
        items: [
          { to: '/finance/tax', label: 'Tax home', icon: ReceiptIcon, end: true, feature: FEATURE.FINANCE_BASIC },
          { to: '/finance/taxes', label: 'Tax codes', icon: ReceiptIcon, feature: FEATURE.FINANCE_BASIC },
          { to: '/finance/compliance', label: 'Global compliance', icon: SecurityIcon, feature: FEATURE.FINANCE_BASIC },
        ],
      },
      {
        id: 'reports',
        label: 'Reports',
        items: [
          {
            to: '/finance/reports',
            label: 'Financial reports',
            icon: AnalyticsIcon,
            end: true,
            feature: FEATURE.FINANCE_ADVANCED_REPORTS,
          },
          {
            to: '/finance/analytics',
            label: 'Enterprise analytics',
            icon: AnalyticsIcon,
            feature: FEATURE.FINANCE_ADVANCED_REPORTS,
          },
          {
            to: '/finance/reports/builder',
            label: 'Custom report builder',
            icon: AnalyticsIcon,
            feature: FEATURE.FINANCE_ADVANCED_REPORTS,
          },
          {
            to: '/finance/revenue',
            label: 'Revenue recognition',
            icon: AnalyticsIcon,
            feature: FEATURE.FINANCE_ACCOUNTING,
          },
        ],
      },
      {
        id: 'administration',
        label: 'Administration',
        items: [
          { to: '/finance/control', label: 'Administration home', icon: SettingsIcon, end: true, feature: FEATURE.FINANCE_BASIC },
          { to: '/finance/setup/wizard', label: 'Setup wizard', icon: SettingsIcon, feature: FEATURE.FINANCE_BASIC },
          { to: '/finance/health', label: 'Health center', icon: FactCheckIcon, feature: FEATURE.FINANCE_BASIC },
          { to: '/finance/close', label: 'Period close', icon: EventNoteIcon, feature: FEATURE.FINANCE_ACCOUNTING },
          { to: '/finance/setup', label: 'Finance setup', icon: SettingsIcon, feature: FEATURE.FINANCE_BASIC },
          { to: '/finance/control/rules', label: 'Business rules', icon: SettingsIcon, feature: FEATURE.FINANCE_ACCOUNTING },
          { to: '/finance/automation', label: 'Automation center', icon: HubIcon, feature: FEATURE.FINANCE_ACCOUNTING },
          { to: '/finance/integrations', label: 'Integration health', icon: HubIcon, feature: FEATURE.FINANCE_BASIC },
          { to: '/finance/audit', label: 'Audit trail', icon: SecurityIcon, feature: FEATURE.FINANCE_ACCOUNTING },
          { to: '/finance/accounting', label: 'Accounting (advanced)', icon: AccountTreeIcon, feature: FEATURE.FINANCE_ACCOUNTING },
          { to: '/finance/ledger', label: 'General ledger', icon: MenuBookIcon, feature: FEATURE.FINANCE_ACCOUNTING },
        ],
      },
      adminGroup('FINANCE'),
    ],
  },
  {
    module: 'PROJECTS',
    label: 'Projects',
    groups: [
      {
        id: 'dashboard',
        label: 'Overview',
        pinned: true,
        items: [
          { to: '/projects', label: 'Dashboard', icon: AssignmentIcon, end: true, feature: FEATURE.PROJECTS_BASIC },
        ],
      },
      {
        id: 'projects',
        label: 'Projects',
        items: [{ to: '/projects/list', label: 'Projects', icon: AssignmentIcon, feature: FEATURE.PROJECTS_BASIC }],
      },
      {
        id: 'tasks',
        label: 'Tasks',
        items: [{ to: '/projects/tasks', label: 'Tasks', icon: FactCheckIcon, feature: FEATURE.PROJECTS_BASIC }],
      },
      {
        id: 'sprints',
        label: 'Sprints',
        items: [{ to: '/projects/sprints', label: 'Sprints', icon: TimelineIcon, feature: FEATURE.PROJECTS_SPRINTS }],
      },
      {
        id: 'time',
        label: 'Time tracking',
        items: [
          { to: '/projects/timesheets', label: 'Timesheets', icon: EventNoteIcon, feature: FEATURE.PROJECTS_TIMESHEETS },
        ],
      },
      {
        id: 'reports',
        label: 'Reports',
        items: [
          {
            to: '/projects/reports',
            label: 'Project analytics',
            icon: AnalyticsIcon,
            feature: FEATURE.PROJECTS_ADVANCED_ANALYTICS,
          },
        ],
      },
      {
        id: 'setup',
        label: 'Configuration',
        items: [
          { to: '/projects/boards', label: 'Boards', icon: ViewKanbanIcon, feature: FEATURE.PROJECTS_BASIC },
          { to: '/projects/issues', label: 'Issues', icon: BugReportIcon, feature: FEATURE.PROJECTS_BASIC },
          { to: '/projects/gantt', label: 'Gantt', icon: TimelineIcon, feature: FEATURE.PROJECTS_GANTT },
          { to: '/projects/calendar', label: 'Calendar', icon: EventNoteIcon, feature: FEATURE.PROJECTS_BASIC },
          { to: '/projects/resources', label: 'Resources', icon: GroupsIcon, feature: FEATURE.PROJECTS_BASIC },
          { to: '/projects/workload', label: 'Workload', icon: GroupsIcon, feature: FEATURE.PROJECTS_ADVANCED_ANALYTICS },
          { to: '/projects/portfolio', label: 'Portfolio / PMO', icon: AnalyticsIcon, feature: FEATURE.PROJECTS_PORTFOLIO },
          { to: '/projects/automation', label: 'Automations', icon: CampaignIcon, feature: FEATURE.PROJECTS_AUTOMATION },
          { to: '/projects/setup', label: 'Project setup', icon: SettingsIcon, feature: FEATURE.PROJECTS_BASIC },
          { to: moduleSharedPath('PROJECTS', 'customers'), label: 'Customers', icon: PeopleIcon },
        ],
      },
      adminGroup('PROJECTS'),
    ],
  },
  {
    module: 'DESK',
    label: 'Help Desk',
    groups: [
      {
        id: 'dashboard',
        label: 'Overview',
        pinned: true,
        items: [
          { to: '/desk', label: 'Dashboard', icon: SupportAgentIcon, end: true, feature: FEATURE.DESK_BASIC },
        ],
      },
      {
        id: 'inbox',
        label: 'Inbox',
        items: [{ to: '/desk/inbox', label: 'Inbox', icon: InboxIcon, feature: FEATURE.DESK_BASIC }],
      },
      {
        id: 'tickets',
        label: 'Tickets',
        items: [
          { to: '/desk/tickets', label: 'Tickets', icon: SupportAgentIcon, feature: FEATURE.DESK_BASIC },
          { to: '/desk/livechat', label: 'Live chat', icon: LiveHelpIcon, feature: FEATURE.DESK_LIVE_CHAT },
        ],
      },
      {
        id: 'knowledge',
        label: 'Knowledge base',
        items: [
          { to: '/desk/knowledgebase', label: 'Knowledge base', icon: MenuBookIcon, feature: FEATURE.DESK_BASIC },
          { to: '/desk/canned-responses', label: 'Canned responses', icon: MenuBookIcon, feature: FEATURE.DESK_BASIC },
        ],
      },
      {
        id: 'work',
        label: 'Work',
        items: [{ to: '/desk/automation', label: 'Automation', icon: CampaignIcon, feature: FEATURE.DESK_AUTOMATION }],
      },
      {
        id: 'reports',
        label: 'Reports',
        items: [
          { to: '/desk/reports', label: 'Support analytics', icon: AnalyticsIcon, feature: FEATURE.DESK_ANALYTICS },
        ],
      },
      {
        id: 'setup',
        label: 'Configuration',
        items: [
          { to: '/desk/setup', label: 'Desk setup', icon: SettingsIcon, feature: FEATURE.DESK_BASIC },
          { to: '/desk/projects', label: 'Desk projects', icon: WorkspacesIcon, feature: FEATURE.DESK_PROJECTS },
          { to: '/desk/boards', label: 'Boards', icon: ViewKanbanIcon, feature: FEATURE.DESK_PROJECTS },
          { to: moduleSharedPath('DESK', 'customers'), label: 'Customers', icon: PeopleIcon },
        ],
      },
      adminGroup('DESK'),
    ],
  },
  {
    module: 'SOCIAL',
    label: 'Social',
    groups: [
      {
        id: 'dashboard',
        label: 'Overview',
        pinned: true,
        items: [{ to: '/social', label: 'Overview', icon: ShareIcon, end: true, feature: FEATURE.SOCIAL_BASIC }],
      },
      {
        id: 'engagement',
        label: 'Unified management',
        items: [
          { to: '/social/inbox', label: 'Unified Inbox', icon: InboxIcon, feature: FEATURE.SOCIAL_INBOX },
          { to: '/social/publishing', label: 'Publishing Center', icon: PublishIcon, feature: FEATURE.SOCIAL_PUBLISHING },
          { to: '/social/calendar', label: 'Content Calendar', icon: EventNoteIcon, feature: FEATURE.SOCIAL_CALENDAR },
          { to: '/social/media', label: 'Media Library', icon: PhotoLibraryIcon, feature: FEATURE.SOCIAL_PUBLISHING },
          { to: '/social/analytics', label: 'Analytics Center', icon: AnalyticsIcon, feature: FEATURE.SOCIAL_ANALYTICS },
          { to: '/social/comments', label: 'Comments Center', icon: LiveHelpIcon, feature: FEATURE.SOCIAL_INBOX },
          { to: '/social/reviews', label: 'Reviews Center', icon: RateReviewIcon, feature: FEATURE.SOCIAL_REPUTATION },
          { to: '/social/automation', label: 'Automation', icon: TimelineIcon, feature: FEATURE.SOCIAL_AUTOMATION },
          { to: '/social/crm-leads', label: 'CRM Leads', icon: PeopleIcon, feature: FEATURE.SOCIAL_BASIC },
          { to: '/social/teams', label: 'Teams', icon: GroupsIcon, feature: FEATURE.SOCIAL_BASIC },
        ],
      },
      {
        id: 'platforms',
        label: 'Connected platforms',
        items: [
          { to: '/social/platforms/facebook', label: 'Facebook', icon: ShareIcon, feature: FEATURE.SOCIAL_BASIC },
          { to: '/social/platforms/instagram', label: 'Instagram', icon: ShareIcon, feature: FEATURE.SOCIAL_BASIC },
          { to: '/social/platforms/whatsapp', label: 'WhatsApp', icon: InboxIcon, feature: FEATURE.SOCIAL_INBOX },
          { to: '/social/platforms/linkedin', label: 'LinkedIn', icon: WorkIcon, feature: FEATURE.SOCIAL_PUBLISHING },
          { to: '/social/platforms/x', label: 'X', icon: ShareIcon, feature: FEATURE.SOCIAL_PUBLISHING },
          { to: '/social/platforms/youtube', label: 'YouTube', icon: ShareIcon, feature: FEATURE.SOCIAL_PUBLISHING },
          { to: '/social/platforms/google_business', label: 'Google Business', icon: BusinessIcon, feature: FEATURE.SOCIAL_REPUTATION },
          { to: '/social/platforms/telegram', label: 'Telegram', icon: InboxIcon, feature: FEATURE.SOCIAL_INBOX },
          { to: '/social/platforms/threads', label: 'Threads', icon: ShareIcon, feature: FEATURE.SOCIAL_PUBLISHING },
          { to: '/social/platforms/pinterest', label: 'Pinterest', icon: ShareIcon, feature: FEATURE.SOCIAL_PUBLISHING },
          { to: '/social/platforms/tiktok', label: 'TikTok', icon: ShareIcon, feature: FEATURE.SOCIAL_PUBLISHING },
        ],
      },
      {
        id: 'listening',
        label: 'Listening & campaigns',
        items: [
          { to: '/social/listening', label: 'Listening', icon: CampaignIcon, feature: FEATURE.SOCIAL_LISTENING },
          { to: '/social/campaigns', label: 'Campaigns', icon: CampaignIcon, feature: FEATURE.SOCIAL_CAMPAIGNS },
        ],
      },
      {
        id: 'setup',
        label: 'Settings',
        items: [
          { to: '/social/connect', label: 'Connection Center', icon: HubIcon, feature: FEATURE.SOCIAL_BASIC },
          { to: '/social/settings', label: 'Settings', icon: SettingsIcon, feature: FEATURE.SOCIAL_BASIC },
        ],
      },
      adminGroup('SOCIAL'),
    ],
  },
];

const byModule = new Map<ErpModule, AppNavConfig>(
  APP_NAV_CONFIGS.map((config) => [config.module, config]),
);

export function getAppNavConfig(module: ErpModule): AppNavConfig | undefined {
  return byModule.get(module);
}

export function getAllAppNavConfigs(): AppNavConfig[] {
  return APP_NAV_CONFIGS;
}

/** `/pos` is the billing terminal only — not a parent of every `/pos/*` path. */
function pathMatchesModulePrefix(pathname: string, prefix: string): boolean {
  if (pathname === prefix) return true;
  if (prefix === '/pos') return false;
  return pathname.startsWith(`${prefix}/`);
}

/** Infer active application from URL — longest matching prefix wins. */
export function resolveModuleFromPath(pathname: string): ErpModule | null {
  const fromAdmin = resolveModuleFromAdminPath(pathname);
  if (fromAdmin) {
    return fromAdmin;
  }

  if (pathname.startsWith('/org/locked/')) {
    const key = pathname.split('/')[3];
    const entry = key ? getModuleEntryBySlug(key) : undefined;
    if (entry) return entry.erpModule;
    return 'POS';
  }

  const lockedParts = pathname.split('/').filter(Boolean);
  if (lockedParts.length === 2 && lockedParts[1] === 'locked') {
    const entry = getModuleEntryBySlug(lockedParts[0]);
    if (entry) return entry.erpModule;
  }

  for (const { fromPrefix, toPrefix } of ALL_LEGACY_ORG_REDIRECTS) {
    if (pathname === fromPrefix || pathname.startsWith(`${fromPrefix}/`)) {
      const entry = MODULE_ENTRIES.find((e) => e.homePath === toPrefix);
      if (entry) return entry.erpModule;
    }
  }

  let best: { module: ErpModule; score: number } | null = null;

  for (const entry of MODULE_ENTRIES) {
    for (const prefix of entry.routePrefixes) {
      if (!pathMatchesModulePrefix(pathname, prefix)) continue;
      const score = prefix.length + 10_000;
      if (!best || score > best.score) {
        best = { module: entry.erpModule, score };
      }
    }
  }

  if (best) return best.module;

  for (const entry of MODULE_ENTRIES) {
    const matchedPrefix = [...entry.sharedRoutePrefixes, ...entry.routePrefixes]
      .filter((p) => pathMatchesModulePrefix(pathname, p))
      .sort((a, b) => b.length - a.length)[0];
    if (matchedPrefix) {
      const score = matchedPrefix.length;
      if (!best || score > best.score) {
        best = { module: entry.erpModule, score };
      }
    }
  }

  return best?.module ?? null;
}
